import type { WorkerBridge } from '../../workers/WorkerBridge.js'
import type { Cart, CartContent, CartLine, CartSummarySnapshot, CartSummaryContentSnapshot } from '../../types/index.js'

const CART_SUMMARY_KEY = 'cart:summary:current'
const DEFAULT_SUMMARY_STALE_TTL_MS = 90_000
const DEFAULT_STORAGE_TTL_MS = 24 * 60 * 60_000

export interface CartSummaryStoreOptions {
  staleTtlMs?: number
  storageTtlMs?: number
}

export class CartSummaryStore {
  private summary: CartSummarySnapshot | null = null
  private readonly staleTtlMs: number
  private readonly storageTtlMs: number

  constructor(
    private readonly bridge: WorkerBridge,
    options?: CartSummaryStoreOptions
  ) {
    this.staleTtlMs = options?.staleTtlMs ?? DEFAULT_SUMMARY_STALE_TTL_MS
    this.storageTtlMs = options?.storageTtlMs ?? DEFAULT_STORAGE_TTL_MS
  }

  peek(): CartSummarySnapshot | null {
    return this.summary
  }

  async get(): Promise<CartSummarySnapshot | null> {
    if (this.summary !== null) {
      return this.summary
    }

    this.summary = await this.bridge.get<CartSummarySnapshot>(CART_SUMMARY_KEY)
    return this.summary
  }

  isStale(summary: CartSummarySnapshot | null): boolean {
    if (summary === null) {
      return true
    }

    return Date.now() >= Date.parse(summary.staleAt)
  }

  async setFromCart(cart: Cart): Promise<CartSummarySnapshot> {
    const now = Date.now()
    const summary = buildCartSummarySnapshot(cart, now, this.staleTtlMs)

    this.summary = summary
    await this.bridge.set(CART_SUMMARY_KEY, summary, this.storageTtlMs)
    return summary
  }

  async clear(): Promise<void> {
    this.summary = null
    await this.bridge.invalidate('^cart:summary:current$')
    await this.bridge.invalidate('^cart:current$')
  }
}

function buildCartSummarySnapshot(cart: Cart, now: number, staleTtlMs: number): CartSummarySnapshot {
  const content = cart.content ?? []
  const topLevelLines = content.flatMap((entry) => getMainLines(entry))

  return {
    cartGuid: cart.guid,
    sourceCartLastModifiedDate: cart.lastModifiedDate,
    cachedAt: new Date(now).toISOString(),
    staleAt: new Date(now + staleTtlMs).toISOString(),
    totalAmountWithTax: cart.totalAmountWithTax,
    totalQuantity: cart.totalQuantity,
    mainReferencesCount: countDistinctReferences(topLevelLines),
    mainQuantity: sumQuantities(topLevelLines),
    contents: content.map((entry) => buildContentSummary(entry)),
  }
}

function buildContentSummary(content: CartContent): CartSummaryContentSnapshot {
  const mainLines = getMainLines(content)

  return {
    contentType: content.contentType,
    totalAmountWithTax: content.totalAmountWithTax,
    mainReferencesCount: countDistinctReferences(mainLines),
    mainLinesCount: mainLines.length,
    mainQuantity: sumQuantities(mainLines),
  }
}

function getMainLines(content: CartContent): CartLine[] {
  return (content.lines ?? []).filter((line) => !line.parentLineId)
}

function countDistinctReferences(lines: CartLine[]): number {
  const keys = new Set<string>()

  for (const line of lines) {
    const key = line.reference?.trim() || line.productGuid
    if (key) {
      keys.add(key)
    }
  }

  return keys.size
}

function sumQuantities(lines: CartLine[]): number {
  return lines.reduce((total, line) => total + line.quantity, 0)
}